<script>
import { getDirectoryItem } from "@/entities/directory";
import { Calendar, RangeCalendar } from "@/shared/ui/calendar";
import Button from "@/shared/ui/button/Button.vue";
import { today, getLocalTimeZone, CalendarDate } from "@internationalized/date";

const DATE_PRESETS = [
  { label: "1 день", duration: { days: 1 } },
  { label: "1 месяц", duration: { months: 1 } },
  { label: "1 год", duration: { years: 1 } },
];

export default {
  name: "ParticipantDropdown",

  components: { Calendar, RangeCalendar, Button },

  data() {
    return {
      visible: false,
      showSecond: false,
      showDatePanel: false,

      allParticipantTypes: [],
      selectedType: null,
      selectedCodes: [],

      onSave: null,
      onDelete: null,
      onRestore: null,
      onNumberToWords: null,
      onPickDate: null,
      isDateField: false,
      datePresets: false,
      dateFormatToggle: false,
      dateRange: false,
      calendarLocale: "ru",
      defaultDateFormat: "words",
      selectedFormat: "words",
      presets: DATE_PRESETS,
      selectedDate: null,
      rangeValue: { start: null, end: null },
      rangePlaceholder: null,
      hasValue: false,

      triggerRect: null,
      position: {
        top: 0,
        left: 0,
      },
    };
  },

  async created() {
    try {
      const data = await getDirectoryItem("participants");
      if (data?.success && data.payload) {
        this.allParticipantTypes = data.payload?.children || [];
      }
    } catch (error) {
      console.error("Ошибка загрузки getDirectoryItem:", error);
    }
  },

  mounted() {
    document.addEventListener("mousedown", this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  },

  computed: {
    participantTypes() {
      if (!this.selectedCodes.length) return [];
      return this.allParticipantTypes.filter((pt) =>
        this.selectedCodes.includes(pt.code)
      );
    },

    selectedDateFormatted() {
      const d = this.selectedDate;
      if (!d) return "";
      const dd = String(d.day).padStart(2, "0");
      const mm = String(d.month).padStart(2, "0");
      return `${dd}.${mm}.${d.year}`;
    },
  },

  methods: {
    openDropdown({
      selectedType,
      selectedCodes,
      onSave,
      onDelete,
      onRestore,
      onNumberToWords,
      onPickDate,
      isDateField,
      datePresets,
      dateFormatToggle,
      dateRange,
      calendarLocale,
      defaultDateFormat,
      currentValue,
      currentDate,
      hasValue,
      rect,
    }) {
      this.selectedType = selectedType;
      this.selectedCodes = selectedCodes || [];
      this.onSave = onSave;
      this.onDelete = onDelete;
      this.onRestore = onRestore;
      this.onNumberToWords = onNumberToWords;
      this.onPickDate = onPickDate || null;
      this.isDateField = Boolean(isDateField);
      this.datePresets = Boolean(datePresets);
      this.dateFormatToggle = Boolean(dateFormatToggle);
      this.dateRange = Boolean(dateRange);
      this.calendarLocale = calendarLocale || "ru";
      this.defaultDateFormat = defaultDateFormat || "words";
      this.hasValue = hasValue;

      this.showSecond = false;
      this.showDatePanel = false;
      this.initDateFromValue(currentValue, currentDate);

      this.triggerRect = rect;
      this.position.top = Math.max(4, rect.bottom + 4) + window.scrollY;
      this.position.left = Math.max(4, rect.left) + window.scrollX;
      this.visible = true;
      this.reposition();
    },

    reposition() {
      this.$nextTick(() => {
        const el = this.$refs.dropdownRef;
        const rect = this.triggerRect;
        if (!el || !rect) return;

        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;
        const { width, height } = el.getBoundingClientRect();

        let top = rect.bottom + 4;
        let left = rect.left;

        if (left + width > viewportW) {
          left = viewportW - width - 8;
        }
        if (top + height > viewportH) {
          const upTop = rect.top - height - 4;
          top = upTop >= 4 ? upTop : Math.max(4, viewportH - height - 8);
        }

        this.position.top = Math.max(4, top) + window.scrollY;
        this.position.left = Math.max(4, left) + window.scrollX;
      });
    },

    select(code) {
      this.onSave?.(code);
      this.close();
    },

    remove() {
      this.onDelete?.();
      this.close();
    },

    restore() {
      this.onRestore?.();
      this.close();
    },

    numberToWords() {
      this.onNumberToWords?.();
      this.close();
    },

    openDatePanel() {
      this.showSecond = false;
      this.showDatePanel = true;
      this.reposition();
    },

    openSecondPanel() {
      this.showSecond = true;
      this.showDatePanel = false;
      this.reposition();
    },

    initDateFromValue(currentValue, currentDate) {
      const v = String(currentValue || "").trim();
      const numeric = v.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      const iso = String(currentDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);

      if (iso) {
        // Машинная дата из mention-узла — точный источник, не зависит от формата.
        this.selectedDate = new CalendarDate(+iso[1], +iso[2], +iso[3]);
        this.selectedFormat = numeric
          ? "numeric"
          : v
          ? "words"
          : this.defaultDateFormat;
      } else if (numeric) {
        this.selectedDate = new CalendarDate(
          +numeric[3],
          +numeric[2],
          +numeric[1]
        );
        this.selectedFormat = "numeric";
      } else if (v) {
        // Прописью распарсить нельзя — оставляем сегодня, запоминаем формат.
        this.selectedDate = today(getLocalTimeZone());
        this.selectedFormat = "words";
      } else {
        this.selectedDate = today(getLocalTimeZone());
        this.selectedFormat = this.defaultDateFormat;
      }

      this.rangePlaceholder = this.selectedDate;
      this.syncRange();
    },

    // Диапазон для expiration_date: начало фиксировано на сегодня, конец — выбранная дата.
    syncRange() {
      const t = today(getLocalTimeZone());
      const end = this.selectedDate || t;
      const ordered = t.compare(end) <= 0;
      this.rangeValue = ordered ? { start: t, end } : { start: end, end: t };
    },

    applyPreset(preset) {
      this.selectedDate = today(getLocalTimeZone()).add(preset.duration);
      this.rangePlaceholder = this.selectedDate;
      this.syncRange();
    },

    onRangeUpdate(range) {
      const picked = range?.end || range?.start;
      if (!picked) return;
      this.selectedDate = picked;
      this.rangePlaceholder = picked;
      this.syncRange();
    },

    confirmDate() {
      const d = this.selectedDate;
      if (d && this.onPickDate) {
        let endDateUnit = null;
        let endDateAmount = null;
        if (this.dateRange) {
          const tz = getLocalTimeZone();
          const ms = d.toDate(tz) - today(tz).toDate(tz);
          endDateUnit = "day";
          endDateAmount = Math.round(ms / 86400000);
        }
        this.onPickDate({
          year: d.year,
          month: d.month,
          day: d.day,
          format: this.dateFormatToggle
            ? this.selectedFormat
            : this.defaultDateFormat,
          endDateUnit,
          endDateAmount,
        });
      }
      this.close();
    },

    handleOutsideClick(e) {
      if (!this.visible) return;
      const dropdown = this.$refs.dropdownRef;
      if (dropdown && !dropdown.contains(e.target)) {
        this.close();
      }
    },

    close() {
      this.visible = false;
      this.showSecond = false;
      this.showDatePanel = false;
    },
  },
};
</script>

<template>
  <Transition name="dropdown">
    <div
      v-if="visible"
      ref="dropdownRef"
      class="fixed z-[9999]"
      :style="{
        top: position.top + 'px',
        left: position.left + 'px',
      }"
    >
      <div
        class="flex bg-popover border border-border rounded-lg shadow-md overflow-hidden"
      >
        <!-- Panel 1: Actions -->
        <div class="min-w-[200px] p-1">
          <button
            v-if="selectedType"
            type="button"
            class="menu-item"
            :class="showSecond && 'bg-accent text-accent-foreground'"
            @click="openSecondPanel"
          >
            <svg-icon
              name="huge-users"
              class="w-4 h-4 shrink-0 text-muted-foreground"
            />
            <span class="flex-1 text-left">{{ $t("Участник") }}</span>
            <svg-icon
              name="huge-chevron-right"
              class="w-4 h-4 shrink-0 opacity-50"
            />
          </button>

          <button
            v-if="hasValue"
            type="button"
            class="menu-item"
            @click="restore"
          >
            <svg-icon
              name="huge-chevron-left"
              class="w-4 h-4 shrink-0 text-muted-foreground"
            />
            <span class="flex-1 text-left">{{ $t("Вернуть ключ") }}</span>
          </button>

          <button
            v-if="isDateField"
            type="button"
            class="menu-item"
            :class="showDatePanel && 'bg-accent text-accent-foreground'"
            @click="openDatePanel"
          >
            <svg-icon
              name="huge-text-config"
              class="w-4 h-4 shrink-0 text-muted-foreground"
            />
            <span class="flex-1 text-left">{{ $t("Дата прописью") }}</span>
            <svg-icon
              name="huge-chevron-right"
              class="w-4 h-4 shrink-0 opacity-50"
            />
          </button>

          <button v-else type="button" class="menu-item" @click="numberToWords">
            <svg-icon
              name="huge-text-config"
              class="w-4 h-4 shrink-0 text-muted-foreground"
            />
            <span class="flex-1 text-left">{{ $t("Число прописью") }}</span>
          </button>

          <button
            type="button"
            class="menu-item text-destructive hover:bg-destructive/10"
            @click="remove"
          >
            <svg-icon name="huge-danger" class="w-4 h-4 shrink-0" />
            <span class="flex-1 text-left">{{ $t("Удалить") }}</span>
          </button>
        </div>

        <!-- Panel: Date picker (Дата прописью) -->
        <Transition name="panel">
          <div
            v-if="showDatePanel"
            class="flex flex-col border-l border-border p-1"
          >
            <div
              v-if="datePresets"
              class="flex flex-col gap-1.5 px-2 pt-2 pb-1"
            >
              <div class="flex items-center justify-between gap-2">
                <div class="text-xs font-medium text-muted-foreground">
                  {{ $t("Срок") }}
                </div>
                <div
                  v-if="selectedDateFormatted"
                  class="text-xs font-medium text-foreground"
                >
                  {{ $t("до") }} {{ selectedDateFormatted }}
                </div>
              </div>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="preset in presets"
                  :key="preset.label"
                  type="button"
                  class="rounded-md border border-border px-2.5 py-1 text-xs text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  @click="applyPreset(preset)"
                >
                  {{ $t(preset.label) }}
                </button>
              </div>
            </div>

            <RangeCalendar
              v-if="dateRange"
              :model-value="rangeValue"
              v-model:placeholder="rangePlaceholder"
              :locale="calendarLocale"
              @update:model-value="onRangeUpdate"
            />
            <Calendar
              v-else
              v-model="selectedDate"
              v-model:placeholder="rangePlaceholder"
              :locale="calendarLocale"
            />

            <div
              v-if="dateFormatToggle"
              class="flex flex-col gap-1.5 px-2 pt-1 pb-5"
            >
              <div class="text-xs font-medium text-muted-foreground">
                {{ $t("Формат") }}
              </div>
              <div class="flex gap-1.5">
                <button
                  type="button"
                  class="flex-1 rounded-md border px-2.5 py-1 text-xs transition-colors"
                  :class="
                    selectedFormat === 'numeric'
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-accent'
                  "
                  @click="selectedFormat = 'numeric'"
                >
                  {{ $t("Цифрами") }}
                </button>
                <button
                  type="button"
                  class="flex-1 rounded-md border px-2.5 py-1 text-xs transition-colors"
                  :class="
                    selectedFormat === 'words'
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-accent'
                  "
                  @click="selectedFormat = 'words'"
                >
                  {{ $t("Прописью") }}
                </button>
              </div>
            </div>

            <Button
              variant="default"
              class="mx-2 mb-1"
              :disabled="!selectedDate"
              @click="confirmDate"
            >
              {{ $t("Вставить") }}
            </Button>
          </div>
        </Transition>

        <!-- Panel 2: Participant types -->
        <Transition name="panel">
          <div
            v-if="showSecond"
            class="min-w-[220px] max-h-[300px] overflow-y-auto border-l border-border p-1"
          >
            <div
              v-if="!participantTypes.length"
              class="flex items-center justify-center px-3 py-6 text-sm text-muted-foreground"
            >
              {{ $t("Тип участника не выбран") }}
            </div>
            <button
              v-for="item in participantTypes"
              :key="item.code"
              type="button"
              class="menu-item"
              :class="
                selectedType === item.code
                  ? 'bg-accent text-accent-foreground font-medium'
                  : ''
              "
              @click="select(item.code)"
            >
              <span class="flex-1 text-left">{{
                item.name || item.name_kz
              }}</span>
              <svg-icon
                v-if="selectedType === item.code"
                name="sli-selected"
                class="w-4 h-4 shrink-0 text-primary"
              />
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.menu-item {
  @apply flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer outline-none transition-colors;
  @apply text-foreground hover:bg-accent hover:text-accent-foreground;
}

/* Dropdown appear/disappear */
.dropdown-enter-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}
.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-2px) scale(0.98);
}

/* Second panel slide */
.panel-enter-active {
  transition: opacity 0.15s ease, width 0.15s ease;
}
.panel-leave-active {
  transition: opacity 0.1s ease, width 0.1s ease;
}
.panel-enter-from {
  opacity: 0;
  width: 0;
  min-width: 0;
  overflow: hidden;
}
.panel-leave-to {
  opacity: 0;
  width: 0;
  min-width: 0;
  overflow: hidden;
}
</style>
